import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Edit, Trash2, Calendar, AlertTriangle, CheckCircle, Eye, User } from "lucide-react";
import { taskService } from "../services/taskService";
import { userService } from "../services/userService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../hooks/use-toast";
import { LoadingCard } from "../components/LoadingSpinner";
import { parseApiError } from "../utils/errorUtils";

const PRIORITY_MAP = {
    low: { label: "Low", color: "bg-blue-500", icon: <CheckCircle className="h-3 w-3" /> },
    medium: { label: "Medium", color: "bg-yellow-500", icon: <AlertTriangle className="h-3 w-3" /> },
    high: { label: "High", color: "bg-red-500", icon: <AlertTriangle className="h-3 w-3 text-red-700" /> },
};

function TaskForm({ task, onSubmit, onCancel, loading }) {
    const [formData, setFormData] = useState({
        title: task?.title || "",
        description: task?.description || "",
        due_date: task?.due_date ? task.due_date.split('T')[0] : "",
        priority: task?.priority || "medium",
        assigned_to: task?.assigned_to || "",
    });

    // Fetch users for assignment
    const { data: usersData, isLoading: usersLoading } = useQuery({
        queryKey: ['users'],
        queryFn: () => userService.getAll()
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (name, value) => {
         setFormData(prev => ({ ...prev, [name]: value }));
    }

    const handleSubmit = (e) => {
        e.preventDefault();
        let taskData = { ...formData };
        
        // Parse due_date as Pacific timezone
        if (taskData.due_date) {
            // If it's a date string (YYYY-MM-DD), keep it as is - backend will handle timezone
            // The backend will interpret it as Pacific time
            if (typeof taskData.due_date === 'string' && taskData.due_date.includes('-')) {
                taskData.due_date = taskData.due_date; // Keep as YYYY-MM-DD
            }
        }
        
        onSubmit(taskData);
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
             <div>
                <Label htmlFor="title">Title *</Label>
                <Input id="title" name="title" value={formData.title} onChange={handleChange} required />
             </div>
             <div>
                <Label htmlFor="description">Description</Label>
                <Textarea id="description" name="description" value={formData.description} onChange={handleChange} rows={3} />
             </div>
             <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <Label htmlFor="due_date">Due Date</Label>
                    <Input id="due_date" name="due_date" type="date" value={formData.due_date} onChange={handleChange} />
                </div>
                <div>
                    <Label htmlFor="priority">Priority</Label>
                    <Select name="priority" value={formData.priority} onValueChange={(value) => handleSelectChange('priority', value)}>
                        <SelectTrigger>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
             </div>
             <div>
                <Label htmlFor="assigned_to">Assign To</Label>
                <Select name="assigned_to" value={formData.assigned_to || "none"} onValueChange={(value) => handleSelectChange('assigned_to', value === "none" ? "" : value)}>
                    <SelectTrigger>
                        <SelectValue placeholder={usersLoading ? "Loading users..." : "Select a user"} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="none">No one (Unassigned)</SelectItem>
                        {usersData?.data?.map((user) => (
                            <SelectItem key={user.id || user._id} value={user.id || user._id}>
                                {user.name} {user.email && `(${user.email})`}
                            </SelectItem>
                        ))}
                        {!usersLoading && (!usersData?.data || usersData.data.length === 0) && (
                            <SelectItem value="no-users" disabled>No users available</SelectItem>
                        )}
                    </SelectContent>
                </Select>
             </div>
             <DialogFooter>
                <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
                <Button type="submit" disabled={loading || !formData.title} className="bg-brand-600 hover:bg-brand-700">
                    {loading ? "Saving..." : (task ? "Update Task" : "Add Task")}
                </Button>
             </DialogFooter>
        </form>
    );
}

function TaskView({ task, onClose, onEdit }) {
    const formatDate = (dateString) => {
        if (!dateString) return "No due date";
        const datePart = dateString.split('T')[0];
        const [year, month, day] = datePart.split('-');
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString();
    };

    const priorityInfo = PRIORITY_MAP[task?.priority] || PRIORITY_MAP.medium;

    // Fetch users to display assignment info
    const { data: usersData, isLoading: usersLoading } = useQuery({
        queryKey: ['users'],
        queryFn: () => userService.getAll()
    });

    const getAssignedUser = () => {
        if (!task?.assigned_to || !usersData?.data) return null;
        return usersData.data.find(u => {
            const userId = u.id || u._id;
            const userIdStr = String(userId);
            const assignedToStr = String(task.assigned_to);
            return userId === task.assigned_to || userIdStr === assignedToStr;
        });
    };

    const getCreatorUser = () => {
        if (!task?.created_by || !usersData?.data) return null;
        
        // Try to find user by comparing both id and _id fields
        // created_by might be stored as MongoDB _id string
        const creator = usersData.data.find(u => {
            const userId = u.id;
            const userMongoId = u._id;
            const createdBy = String(task.created_by);
            
            // Match against both id and _id (as string)
            return userId === createdBy || String(userMongoId) === createdBy;
        });
        
        return creator;
    };

    const assignedUser = getAssignedUser();
    const creatorUser = getCreatorUser();

    return (
        <div className="space-y-4">
            <div>
                <Label className="text-sm font-semibold text-gray-600">Title</Label>
                <p className="mt-1 text-base">{task?.title || "N/A"}</p>
            </div>

            <div>
                <Label className="text-sm font-semibold text-gray-600">Description</Label>
                <p className="mt-1 text-base whitespace-pre-wrap">{task?.description || "No description provided"}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <Label className="text-sm font-semibold text-gray-600">Due Date</Label>
                    <p className="mt-1 text-base">{formatDate(task?.due_date)}</p>
                </div>

                <div>
                    <Label className="text-sm font-semibold text-gray-600">Priority</Label>
                    <div className="mt-1">
                        <Badge variant="outline" className={`border-none text-white ${priorityInfo.color}`}>
                            {priorityInfo.icon}
                            <span className="ml-1">{priorityInfo.label}</span>
                        </Badge>
                    </div>
                </div>
            </div>

            <div>
                <Label className="text-sm font-semibold text-gray-600">Status</Label>
                <p className="mt-1 text-base">
                    {task?.is_complete ? (
                        <span className="text-green-600 font-medium">✓ Completed</span>
                    ) : (
                        <span className="text-gray-600">Incomplete</span>
                    )}
                </p>
            </div>

            {/* Assignment Information - Always show this section */}
            <div className="border-t pt-4 space-y-3">
                {assignedUser && (
                    <div>
                        <Label className="text-sm font-semibold text-gray-600">Assigned To</Label>
                        <div className="mt-1 text-sm flex items-center">
                            <User className="mr-2 h-4 w-4 text-gray-400" />
                            {assignedUser.name} {assignedUser.email && `(${assignedUser.email})`}
                        </div>
                    </div>
                )}
                <div>
                    <Label className="text-sm font-semibold text-gray-600">Created By</Label>
                    <div className="mt-1 text-sm flex items-center text-gray-600">
                        <User className="mr-2 h-4 w-4 text-gray-400" />
                        {usersLoading ? (
                            <span className="text-gray-500 italic">Loading user...</span>
                        ) : creatorUser ? (
                            <span>{creatorUser.name} {creatorUser.email && `(${creatorUser.email})`}</span>
                        ) : task?.created_by ? (
                            <span className="text-gray-500 italic">User not found (ID: {task.created_by})</span>
                        ) : (
                            <span className="text-gray-500 italic">Unknown (task created before tracking was added)</span>
                        )}
                    </div>
                </div>
            </div>

            <DialogFooter className="mt-6">
                <Button variant="outline" onClick={onClose}>
                    Close
                </Button>
                <Button onClick={onEdit} className="bg-brand-600 hover:bg-brand-700">
                    <Edit className="mr-2 h-4 w-4" /> Edit Task
                </Button>
            </DialogFooter>
        </div>
    );
}


export default function Tasks() {
  const [filterComplete, setFilterComplete] = useState("incomplete");
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  const { data: tasksData, isLoading: loading, refetch } = useQuery({
    queryKey: ['tasks', filterComplete],
    queryFn: () => taskService.getAll({
      is_complete: filterComplete === 'all' ? undefined : (filterComplete === 'complete'),
      sort_by: 'due_date',
      sort_dir: 1
    })
  });

  const createTaskMutation = useMutation({
    mutationFn: (taskData) => taskService.create(taskData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast({ description: "Task added successfully!" });
      setIsAddDialogOpen(false);
    },
    onError: (error) => {
      console.error("Task creation error:", error);
      const errorMessage = parseApiError(error, "Failed to create task");
      toast({ 
        variant: "destructive",
        description: errorMessage
      });
    }
  });

  const updateTaskMutation = useMutation({
    mutationFn: ({ id, data }) => taskService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast({ description: "Task updated successfully!" });
      setIsEditDialogOpen(false);
      setEditingTask(null);
    },
    onError: (error) => {
      console.error("Task update error:", error);
      const errorMessage = parseApiError(error, "Failed to update task");
      toast({ 
        variant: "destructive",
        description: errorMessage
      });
    }
  });

  const deleteTaskMutation = useMutation({
    mutationFn: (taskId) => taskService.delete(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast({ description: "Task deleted successfully!" });
    },
    onError: (error) => {
      console.error("Task deletion error:", error);
      const errorMessage = parseApiError(error, "Failed to delete task");
      toast({ 
        variant: "destructive",
        description: errorMessage
      });
    }
  });

  const toggleCompleteMutation = useMutation({
    mutationFn: ({ id, complete }) => taskService.toggleComplete(id, complete),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast({ 
        description: `Task marked as ${variables.complete ? 'complete' : 'incomplete'}!` 
      });
    },
    onError: (error) => {
      toast({ 
        variant: "destructive",
        description: error?.detail || "Failed to update task status" 
      });
    }
  });

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [viewingTask, setViewingTask] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingTaskId, setDeletingTaskId] = useState(null);

  const tasks = tasksData?.data || [];

  const handleAddTask = async (taskData) => {
    createTaskMutation.mutate(taskData);
  };

  const handleViewTask = (task) => {
    setViewingTask(task);
    setIsViewDialogOpen(true);
  };

  const handleEditFromView = () => {
    setIsViewDialogOpen(false);
    setEditingTask(viewingTask);
    setIsEditDialogOpen(true);
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setIsEditDialogOpen(true);
  };

  const handleUpdateTask = async (taskData) => {
    updateTaskMutation.mutate({ 
      id: editingTask.id || editingTask._id, 
      data: taskData 
    });
  };

  const handleDeleteTask = async (taskId) => {
    setDeletingTaskId(taskId);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteTask = async () => {
    if (!deletingTaskId) return;
    deleteTaskMutation.mutate(deletingTaskId);
    setIsDeleteDialogOpen(false);
    setDeletingTaskId(null);
  };


  const handleToggleComplete = async (task) => {
    const newStatus = !task.is_complete;
    toggleCompleteMutation.mutate({ 
      id: task.id || task._id, 
      complete: newStatus 
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "No due date";
    // Parse the date without timezone conversion
    // Extract just the date part (YYYY-MM-DD) to avoid timezone shifts
    const datePart = dateString.split('T')[0]; // Get "2025-01-15" from "2025-01-15T00:00:00.000Z"
    const [year, month, day] = datePart.split('-');
    // Create a date using local timezone
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString();
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Tasks</h1>
          <p className="text-gray-600 mt-1">Manage your to-do items</p>
        </div>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
                <Button className="bg-brand-600 hover:bg-brand-700 w-full sm:w-auto shrink-0">
                  <Plus className="mr-2 h-4 w-4" /> Add Task
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>Add New Task</DialogTitle>
                </DialogHeader>
                <TaskForm onSubmit={handleAddTask} onCancel={() => setIsAddDialogOpen(false)} loading={createTaskMutation.isPending} />
            </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Your Tasks</CardTitle>
           <div className="flex space-x-2 pt-2">
                <Button variant={filterComplete === 'incomplete' ? 'default': 'outline'} size="sm" onClick={() => setFilterComplete('incomplete')}>Incomplete</Button>
                <Button variant={filterComplete === 'complete' ? 'default': 'outline'} size="sm" onClick={() => setFilterComplete('complete')}>Complete</Button>
                <Button variant={filterComplete === 'all' ? 'default': 'outline'} size="sm" onClick={() => setFilterComplete('all')}>All</Button>
           </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <LoadingCard />
          ) : tasks.length === 0 ? (
             <p className="text-center text-gray-500 py-8">No tasks found matching filter.</p>
          ) : (
            <>
              {/* Mobile card view */}
              <div className="md:hidden space-y-3">
                {tasks.map((task) => {
                  const priorityInfo = PRIORITY_MAP[task.priority] || PRIORITY_MAP.medium;
                  return (
                    <div key={task.id || task._id} className={`border rounded-lg p-4 ${task.is_complete ? 'opacity-60' : ''}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <Checkbox
                            checked={task.is_complete}
                            onCheckedChange={() => handleToggleComplete(task)}
                            disabled={toggleCompleteMutation.isPending}
                            className="mt-1 shrink-0"
                          />
                          <div className="min-w-0">
                            <p className={`font-medium ${task.is_complete ? 'line-through text-gray-400' : ''}`}>
                              {task.title}
                            </p>
                            <div className="flex flex-wrap items-center gap-2 mt-2">
                              <span className={`text-sm ${task.is_complete ? 'text-gray-400' : 'text-gray-600'}`}>
                                <Calendar className="inline mr-1 h-3 w-3" />
                                {formatDate(task.due_date)}
                              </span>
                              <Badge variant="outline" className={`border-none text-white ${priorityInfo.color}`}>
                                {priorityInfo.icon}
                                <span className="ml-1">{priorityInfo.label}</span>
                              </Badge>
                            </div>
                          </div>
                        </div>
                        <div className="flex shrink-0">
                          <Button variant="ghost" size="sm" onClick={() => handleViewTask(task)} className="text-blue-600 hover:text-blue-700 h-8 w-8 p-0">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleEditTask(task)} disabled={updateTaskMutation.isPending} className="h-8 w-8 p-0">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteTask(task.id || task._id)} disabled={deleteTaskMutation.isPending} className="text-red-600 hover:text-red-700 h-8 w-8 p-0">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop table view */}
              <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">Status</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tasks.map((task) => {
                  const priorityInfo = PRIORITY_MAP[task.priority] || PRIORITY_MAP.medium;
                  return (
                    <TableRow key={task.id || task._id}>
                      <TableCell>
                        <Checkbox
                          checked={task.is_complete}
                          onCheckedChange={() => handleToggleComplete(task)}
                          disabled={toggleCompleteMutation.isPending}
                        />
                      </TableCell>
                      <TableCell className={`font-medium ${task.is_complete ? 'line-through text-gray-400' : ''}`}>
                        {task.title}
                      </TableCell>
                      <TableCell className={task.is_complete ? 'text-gray-400' : ''}>{formatDate(task.due_date)}</TableCell>
                      <TableCell>
                         <Badge variant="outline" className={`border-none text-white ${priorityInfo.color}`}>
                            {priorityInfo.icon}
                            <span className="ml-1">{priorityInfo.label}</span>
                         </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => handleViewTask(task)} className="text-blue-600 hover:text-blue-700">
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleEditTask(task)} disabled={updateTaskMutation.isPending}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteTask(task.id || task._id)} disabled={deleteTaskMutation.isPending} className="text-red-600 hover:text-red-700">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* View Task Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={(open) => {
        setIsViewDialogOpen(open);
        if (!open) setViewingTask(null);
      }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Task Details</DialogTitle>
          </DialogHeader>
          {viewingTask && (
            <TaskView
              task={viewingTask}
              onClose={() => {
                setIsViewDialogOpen(false);
                setViewingTask(null);
              }}
              onEdit={handleEditFromView}
            />
          )}
        </DialogContent>
      </Dialog>

       <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>Edit Task</DialogTitle>
                </DialogHeader>
                {editingTask && (
                    <TaskForm
                        task={editingTask}
                        onSubmit={handleUpdateTask}
                        onCancel={() => { setIsEditDialogOpen(false); setEditingTask(null); }}
                        loading={updateTaskMutation.isPending}
                    />
                )}
            </DialogContent>
        </Dialog>

        {/* Delete Task Confirmation Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle>Delete Task</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-gray-600">
                Are you sure you want to delete this task? This action cannot be undone.
              </p>
            </div>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setIsDeleteDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={confirmDeleteTask}
                disabled={deleteTaskMutation.isPending}
              >
                {deleteTaskMutation.isPending ? "Deleting..." : "Delete"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
    </div>
  );
}
